import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  Spinner,
} from '@/dc-shared-web/src'
import { Combobox } from '@dc-shared-web/components/base/ComboBox'
import { UseFormReturn } from 'react-hook-form'
import { Portfolio, Project, Contract } from '@/integrations/trimble/types'

interface DestinationFieldsProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  form: UseFormReturn<any>
  portfolios: Portfolio[]
  projects: Project[]
  contracts: Contract[]
  loadingPortfolios: boolean
  loadingProjects: boolean
  loadingContracts: boolean
  watchPortfolio: string
  watchProject: string
}

// Helper functions to map data to options
const mapPortfolioToOption = (portfolio: Portfolio) => ({
  value: portfolio.PortfolioID || '',
  label: portfolio.Name,
})

const mapProjectToOption = (project: Project) => ({
  value: project.ProjectID?.toString() || '',
  label: project.Name,
})

const mapContractToOption = (contract: Contract) => ({
  value: contract.ContractID?.toString() || '',
  label: `${contract.ContractNumber} - ${contract.Description}`,
})

/**
 * Component for rendering destination fields (portfolio, project, contract)
 */
export const DestinationFields: React.FC<DestinationFieldsProps> = ({
  form,
  portfolios,
  projects,
  contracts,
  loadingPortfolios,
  loadingProjects,
  loadingContracts,
  watchPortfolio,
  watchProject,
}) => {
  return (
    <section
      className={'tw-bg-subtle tw-mt-4 tw-p-3'}
      aria-labelledby='destination-heading'
    >
      <h3
        className='tw-uppercase tw-text-neutral-500 tw-text-sm tw-mb-3'
        id='destination-heading'
      >
        Destination
      </h3>
      <FormField
        control={form.control}
        name='portfolio'
        render={({ field }) => (
          <FormItem className={'tw-space-y-3'}>
            <FormLabel required className='tw-text-[#1C1C1E]'>
              Portfolio
            </FormLabel>
            <FormControl>
              <div>
                <Combobox
                  placeholder={
                    loadingPortfolios
                      ? 'Loading portfolios...'
                      : 'Select a portfolio'
                  }
                  options={portfolios.map(mapPortfolioToOption)}
                  selectedId={field.value}
                  onChange={field.onChange}
                  widthClass='tw-w-full'
                  disabled={loadingPortfolios}
                  aria-label='Portfolio selection'
                  data-testid='ps_portfolio'
                />
                {loadingPortfolios && (
                  <div
                    className='tw-flex tw-items-center tw-mt-1'
                    aria-live='polite'
                  >
                    <Spinner
                      className='tw-h-4 tw-w-4 tw-mr-2'
                      aria-hidden='true'
                    />
                    <span className='tw-text-xs tw-text-neutral-500'>
                      Loading portfolios...
                    </span>
                  </div>
                )}
              </div>
            </FormControl>
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name='project'
        render={({ field }) => (
          <FormItem className={'tw-space-y-3'}>
            <FormLabel required className='tw-text-[#1C1C1E]'>
              Project
            </FormLabel>
            <FormControl>
              <div>
                <Combobox
                  placeholder={
                    loadingProjects ? 'Loading projects...' : 'Select a project'
                  }
                  options={projects.map(mapProjectToOption)}
                  selectedId={field.value}
                  onChange={field.onChange}
                  widthClass='tw-w-full'
                  disabled={!watchPortfolio || loadingProjects}
                  aria-label='Project selection'
                  data-testid='ps_project'
                />
                {loadingProjects && (
                  <div
                    className='tw-flex tw-items-center tw-mt-1'
                    aria-live='polite'
                  >
                    <Spinner
                      className='tw-h-4 tw-w-4 tw-mr-2'
                      aria-hidden='true'
                    />
                    <span className='tw-text-xs tw-text-neutral-500'>
                      Loading projects...
                    </span>
                  </div>
                )}
              </div>
            </FormControl>
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name='contract'
        render={({ field }) => (
          <FormItem className={'tw-space-y-3'}>
            <FormLabel required className='tw-text-[#1C1C1E]'>
              Contract
            </FormLabel>
            <FormControl>
              <div>
                <Combobox
                  placeholder={
                    loadingContracts
                      ? 'Loading contracts...'
                      : 'Select a contract'
                  }
                  options={contracts.map(mapContractToOption)}
                  selectedId={field.value}
                  onChange={field.onChange}
                  widthClass='tw-w-full'
                  disabled={
                    !watchPortfolio || !watchProject || loadingContracts
                  }
                  aria-label='Contract selection'
                  data-testid='ps_contract'
                />
                {loadingContracts && (
                  <div
                    className='tw-flex tw-items-center tw-mt-1'
                    aria-live='polite'
                  >
                    <Spinner
                      className='tw-h-4 tw-w-4 tw-mr-2'
                      aria-hidden='true'
                    />
                    <span className='tw-text-xs tw-text-neutral-500'>
                      Loading contracts...
                    </span>
                  </div>
                )}
              </div>
            </FormControl>
          </FormItem>
        )}
      />
    </section>
  )
}

export default DestinationFields
